import { RequestStatusEnum } from "@/lib/enum/request-status-enum";
import { MrfiktivHttpClientProvider } from "@/services/mrfiktiv/mrfiktiv-http-client.provider";
import {
  MrfiktivClearanceApprovalViewModelGen,
  MrfiktivCreateClearanceApprovalDtoGen,
  MrfiktivPageViewModelGen,
  MrfiktivClearanceApprovalControllerGetParamsGen,
  MrfiktivUpdateClearanceApprovalDtoGen
} from "../v1/data-contracts";
import { ClearanceApproval } from "../v1/ClearanceApproval"; // Adjusted import

/**
 * Communicate with ClearanceApproval API
 */
class ClearanceApprovalService {
  mrfiktivProxy: ClearanceApproval;

  /**
   * Construct service
   */
  constructor(mrfiktivHttpClientProvider: MrfiktivHttpClientProvider) {
    this.mrfiktivProxy = new ClearanceApproval(mrfiktivHttpClientProvider.client());
  }

  /**
   * Create a ClearanceApproval
   */
  async create(
    partnerId: string,
    data: MrfiktivCreateClearanceApprovalDtoGen
  ): Promise<MrfiktivClearanceApprovalViewModelGen> {
    const clearanceApproval = (await this.mrfiktivProxy.clearanceApprovalControllerCreate(partnerId, data)).data;
    return clearanceApproval;
  }

  /**
   * Get all ClearanceApprovals
   */
  async get(data: MrfiktivClearanceApprovalControllerGetParamsGen): Promise<
    MrfiktivPageViewModelGen & {
      data?: MrfiktivClearanceApprovalViewModelGen[];
    }
  > {
    const clearanceApprovals = (await this.mrfiktivProxy.clearanceApprovalControllerGet(data)).data;
    return clearanceApprovals;
  }

  /**
   * Get a ClearanceApproval
   */
  async getOne(partnerId: string, clearanceApprovalId: string): Promise<MrfiktivClearanceApprovalViewModelGen> {
    const clearanceApproval = (
      await this.mrfiktivProxy.clearanceApprovalControllerGetOne(partnerId, clearanceApprovalId)
    ).data;
    return clearanceApproval;
  }

  /**
   * Update a ClearanceApproval
   */
  async update(
    partnerId: string,
    clearanceApprovalId: string,
    data: MrfiktivUpdateClearanceApprovalDtoGen
  ): Promise<MrfiktivClearanceApprovalViewModelGen> {
    const clearanceApproval = (
      await this.mrfiktivProxy.clearanceApprovalControllerUpdate(partnerId, clearanceApprovalId, data)
    ).data;
    return clearanceApproval;
  }

  /**
   * Update status of a ClearanceApproval
   */
  async updateStatus(
    partnerId: string,
    clearanceApprovalId: string,
    status: RequestStatusEnum // Assuming RequestStatusEnum is compatible with MrfiktivUpdateClearanceApprovalStatusDtoGen
  ): Promise<MrfiktivClearanceApprovalViewModelGen> {
    const shared = await this.mrfiktivProxy.clearanceApprovalControllerUpdateStatus(partnerId, clearanceApprovalId, {
      status // This matches MrfiktivUpdateClearanceApprovalStatusDtoGen
    });
    return shared.data;
  }

  /**
   * Delete a ClearanceApproval
   */
  async delete(partnerId: string, clearanceApprovalId: string): Promise<MrfiktivClearanceApprovalViewModelGen> {
    const clearanceApproval = (
      await this.mrfiktivProxy.clearanceApprovalControllerDelete(partnerId, clearanceApprovalId)
    ).data;
    return clearanceApproval;
  }
}

/**
 * Communicate with ClearanceApproval API
 */
export default new ClearanceApprovalService(new MrfiktivHttpClientProvider());
